import { tkPersonName } from "./formSubject";

const pad = (value) => String(value).padStart(2, "0");

export const formatDate = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleDateString("en-nz");
};

export const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    return date.toLocaleString("en-nz");
};

export const toDDMMYYYY = (value) => {
    if (value === null || value === undefined || value === "") return "";
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
};

export const toSubmitDate = (ddmmyyyy) => {
    const [day, month, year] = String(ddmmyyyy || "").split("/").map(Number);
    if ([day, month, year].some(Number.isNaN)) return null;
    const date = new Date(year, month - 1, day, 12, 0, 0);
    if (date.getFullYear() !== year || date.getMonth() + 1 !== month || date.getDate() !== day) return null;
    const now = new Date();
    return date.toDateString() === now.toDateString() ? now.toISOString() : date.toISOString();
};

export const toDateTimeLocal = (value) => {
    const date = value instanceof Date ? value : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

export const fromDateTimeLocal = (value) => {
    if (!value) return null;
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? null : date.toISOString();
};

export const dateOnlyFromDateTimeLocal = (value) => {
    const match = /^(\d{4}-\d{2}-\d{2})T\d{2}:\d{2}/.exec(String(value || ""));
    return match ? match[1] : null;
};

export const statusColor = (statusCode) => ({
    waitlist: "orange",
    active: "positive",
    exited: "grey-7",
})[statusCode] || "grey-7";

export const needsTierOptions = [
    {
        label: "Tier 1 – Low Needs (light-touch, progressing less urgent needs/goals)",
        value: "Tier 1",
    },
    {
        label: "Tier 2 – Moderate Needs (fortnightly or group engagement to address moderate needs/goals)",
        value: "Tier 2",
    },
    {
        label: "Tier 3 – High & Complex Needs (daily or weekly engagement to address urgent needs/goals)",
        value: "Tier 3",
    },
];

export const needsTierColor = (needsTier) => ({
    "Tier 1": "positive",
    "Tier 2": "warning",
    "Tier 3": "negative",
})[needsTier] || "grey-7";

export const needsTierLabel = (needsTier) => ({
    "Tier 1": "Tier 1 – Low Needs",
    "Tier 2": "Tier 2 – Moderate Needs",
    "Tier 3": "Tier 3 – High & Complex Needs",
})[needsTier] || "-";

export const needsReminderDetails = (engagement, comparisonDate) => {
    const needsTier = engagement?.needs_tier || engagement?.risk_factor;
    const reviewedAt = engagement?.active_reminder_reviewed_at
        || engagement?.activated_at
        || engagement?.entered_at;
    if (!needsTier || !reviewedAt) return null;

    const dueAt = new Date(reviewedAt);
    if (Number.isNaN(dueAt.getTime())) return null;
    if (needsTier === "Tier 1") dueAt.setMonth(dueAt.getMonth() + 1);
    if (needsTier === "Tier 2") dueAt.setDate(dueAt.getDate() + 14);
    if (needsTier === "Tier 3") dueAt.setDate(dueAt.getDate() + 7);

    const today = comparisonDate instanceof Date ? new Date(comparisonDate) : new Date();
    today.setHours(0, 0, 0, 0);
    const dueDay = new Date(dueAt);
    dueDay.setHours(0, 0, 0, 0);
    return {
        dueAt: dueAt.toISOString(),
        dueLabel: formatDate(dueAt),
        isDue: today.getTime() >= dueDay.getTime(),
    };
};

export const staffName = (staff) => {
    if (!staff) return "";
    return staff.name
        || staff.assigned_worker_name
        || staff.staff_name
        || [staff.first_name, staff.last_name].filter(Boolean).join(" ").trim()
        || staff.email
        || staff.assigned_worker_email
        || "-";
};

export const staffOption = (staff) => ({
    label: staffName(staff),
    value: staff.id,
});

export const ageFromDateOfBirth = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    const today = new Date();
    const birthdayThisYear = new Date(today.getFullYear(), date.getMonth(), date.getDate());
    const age = today.getFullYear() - date.getFullYear() - (today < birthdayThisYear ? 1 : 0);
    return age >= 0 ? String(age) : "-";
};

export const personFullName = (person) => [person?.first_name, person?.last_name]
    .map(value => String(value || "").trim())
    .filter(Boolean)
    .join(" ");

export const engagementPersonName = (person) => {
    const preferredName = String(person?.preferred_name || "").trim();
    const normalizedPerson = {
        ...person,
        preferred_name: preferredName,
        first_name: String(person?.first_name || "").trim(),
        last_name: String(person?.last_name || "").trim(),
    };
    const fullName = personFullName(normalizedPerson);
    if (
        preferredName
        && fullName
        && preferredName.localeCompare(fullName, undefined, { sensitivity: "accent" }) !== 0
    ) {
        return `${preferredName} (${fullName})`;
    }
    return preferredName || fullName || tkPersonName(normalizedPerson) || "-";
};

export const personPhoneNumbers = (person) => {
    const stored = Array.isArray(person?.phone_numbers)
        ? person.phone_numbers
            .filter(phone => String(phone?.phone_number || "").trim())
            .map((phone, index) => ({
                ...phone,
                phone_type: String(phone.phone_type || "Other").trim() || "Other",
                phone_number: String(phone.phone_number).trim(),
                sort_order: Number.isInteger(phone.sort_order) ? phone.sort_order : index,
            }))
        : [];
    if (stored.length) return stored;

    const legacyPhone = String(person?.phone || "").trim();
    return legacyPhone
        ? [{ id: null, phone_type: "Other", phone_number: legacyPhone, sort_order: 0 }]
        : [];
};

export const personContactRows = (person) => {
    const phoneRows = personPhoneNumbers(person).map(phone => ({
        label: `${phone.phone_type} phone`,
        value: phone.phone_number,
        href: `tel:${phone.phone_number}`,
    }));
    return [
        ...(phoneRows.length ? phoneRows : [{ label: "Phone", value: "-", href: null }]),
        {
            label: "Email",
            value: person?.email || "-",
            href: person?.email ? `mailto:${person.email}` : null,
        },
    ];
};

export const personEmergencyContacts = (person) => {
    const stored = Array.isArray(person?.emergency_contacts)
        ? person.emergency_contacts
            .filter(contact => contact && (contact.name || contact.relationship || contact.phone))
            .map((contact, index) => ({
                ...contact,
                name: String(contact.name || "").trim() || null,
                relationship: String(contact.relationship || "").trim() || null,
                phone: String(contact.phone || "").trim() || null,
                permission_to_contact: contact.permission_to_contact === true,
                sort_order: Number.isInteger(contact.sort_order) ? contact.sort_order : index,
            }))
        : [];
    if (stored.length) return stored;

    const legacyContact = {
        id: null,
        name: String(person?.emergency_contact_name || "").trim() || null,
        relationship: String(person?.emergency_contact_relationship || "").trim() || null,
        phone: String(person?.emergency_contact_phone || "").trim() || null,
        permission_to_contact: false,
        sort_order: 0,
    };
    return legacyContact.name || legacyContact.relationship || legacyContact.phone
        ? [legacyContact]
        : [];
};

export const formatMinutes = (value) => {
    const minutes = Number(value);
    if (!Number.isFinite(minutes) || minutes < 0) return "-";
    const hours = Math.floor(minutes / 60);
    const remainder = minutes % 60;
    const hourLabel = hours === 1 ? "hour" : "hours";
    const minuteLabel = remainder === 1 ? "minute" : "minutes";
    return `${hours} ${hourLabel} ${remainder} ${minuteLabel}`;
};

export const groupAttendanceSessionLabel = (attendance) => {
    const attended = Number(attendance?.sessions_attended);
    const total = Number(attendance?.total_sessions);
    const safeAttended = Number.isFinite(attended) && attended >= 0 ? attended : 0;
    const safeTotal = Number.isFinite(total) && total >= 0 ? total : 0;
    return `${safeAttended} of ${safeTotal}`;
};

export const groupAttendancePercentage = (attendance) => {
    const total = Number(attendance?.total_sessions);
    if (!Number.isFinite(total) || total <= 0) return null;

    const rawPercentage = attendance?.attendance_percentage;
    if (rawPercentage == null || (typeof rawPercentage === "string" && rawPercentage.trim() === "")) return null;

    const percentage = Number(rawPercentage);
    if (!Number.isFinite(percentage)) return null;
    return Math.min(100, Math.max(0, percentage));
};

export const groupAttendancePercentageLabel = (attendance) => {
    const percentage = groupAttendancePercentage(attendance);
    if (percentage == null) return "-";
    return `${Math.round(percentage)}%`;
};

export const groupAttendanceProgressColor = (attendance) => {
    const percentage = groupAttendancePercentage(attendance);
    if (percentage == null) return "grey-6";
    if (percentage >= 80) return "positive";
    if (percentage >= 50) return "warning";
    return "negative";
};

const additionalWorkersFromRow = (row) => {
    const workers = Array.isArray(row.additional_workers)
        ? row.additional_workers
        : row.secondary_worker
            ? [{
                staff_id: row.secondary_worker,
                engagement_role: "secondary",
                worker_name: row.secondary_worker_name,
                worker_email: row.secondary_worker_email,
            }]
            : [];
    return workers.map(worker => ({
        ...worker,
        workerName: worker.worker_name || worker.worker_email || "-",
    }));
};

export const mapEngagementRow = (row) => ({
    ...row,
    additionalWorkers: additionalWorkersFromRow(row),
    emergency_contacts: personEmergencyContacts(row),
    personName: engagementPersonName(row),
    dateOfBirth: formatDate(row.date_of_birth),
    age: ageFromDateOfBirth(row.date_of_birth),
    enteredAt: formatDate(row.entered_at),
    activatedAt: formatDate(row.activated_at),
    exitedAt: formatDate(row.exited_at),
    signedOffAt: formatDate(row.signed_off_at),
    signedOffByName: row.signed_off_by_name || "-",
    lastContactAt: formatDate(row.last_contact_at),
    timeSpent: formatMinutes(row.total_time_worked_minutes),
    primaryWellbeingTitle: row.primary_wellbeing_title || "-",
    statusLabel: row.status_name || row.status_code || "-",
    lifecycleLabel: row.status_name || row.status_code || "-",
    needsTierLabel: needsTierLabel(row.needs_tier || row.risk_factor),
    riskAssessmentAt: formatDate(row.risk_assessment_at),
    activeReminderReviewedAt: formatDate(row.active_reminder_reviewed_at),
    activeReminder: needsReminderDetails(row, new Date()),
    assignedWorkerName: row.assigned_worker_name || "-",
    secondaryWorkerName: row.secondary_worker_name || "-",
    activatedByName: row.activated_by_name || "-",
    referralSource: row.referral_source_name ? `${row.referral_source} - ${row.referral_source_name}` : row.referral_source || "-",
    exitReason: row.exit_reason || "-",
    referredTo: row.referred_to_name || "-",
    updatedAt: formatDateTime(row.updated_at),
});

export const personFromEngagement = (engagement) => ({
    id: engagement?.tk_person_id,
    first_name: engagement?.first_name,
    last_name: engagement?.last_name,
    preferred_name: engagement?.preferred_name,
    date_of_birth: engagement?.date_of_birth,
    gender_id: engagement?.gender_id,
    ethnicity_id: engagement?.ethnicity_id,
    iwi_id: engagement?.iwi_id,
    nhi: engagement?.nhi,
    phone: engagement?.phone,
    phone_numbers: personPhoneNumbers(engagement),
    email: engagement?.email,
    address_line_1: engagement?.address_line_1,
    suburb: engagement?.suburb,
    city: engagement?.city,
    postcode: engagement?.postcode,
    sa2_code: engagement?.sa2_code,
    emergency_contact_name: engagement?.emergency_contact_name,
    emergency_contact_relationship: engagement?.emergency_contact_relationship,
    emergency_contact_phone: engagement?.emergency_contact_phone,
    emergency_contacts: personEmergencyContacts(engagement),
});
